import { AbilityProvider } from '@casl/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { buildAbilityFromRules, unpackAbilityRules } from '@/hooks/useAbility';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';

/**
 * Publishes the current user's server-driven CASL ability to the tree, so
 * `useAbility` and `<Can>` resolve anywhere below it.
 *
 * Holds one ability instance for as long as it stays mounted and feeds new
 * rules through `ability.update` rather than replacing the instance:
 * `useAbility` subscribes to that instance's `updated` event, so mutating in
 * place is what propagates a permission change to consumers. Mount this above
 * anything that can render, not inside a subtree that unmounts, since
 * `useAbility` throws when no provider sits above it.
 *
 * `userId` is a prop rather than read from a store, which keeps this component
 * independent of how identity is held. Pass the acting (impersonation-aware) id
 * so an impersonated session gets the impersonated user's rules, matching what
 * `/v2/users/me` returns under the impersonation header.
 *
 * The instance is seeded from whatever rules are already cached rather than
 * starting empty and filling in from the effect. `/v2/users/me` is usually
 * cached by the time a screen mounts, and a passive effect runs after the
 * browser may already have painted, which would flash a gated control absent
 * and then pop it in. With no cached rules there is nothing to seed, so a cold
 * start still fails closed.
 *
 * Mounting the query here costs no extra request: it shares
 * `getCurrentUserV2QueryOptions`' key with the many screens already fetching
 * it, so React Query dedupes.
 */
export function AppAbilityProvider({ userId, children }: { userId?: string; children: ReactNode }) {
  const { data: currentUser } = useQuery(getCurrentUserV2QueryOptions(userId));

  const rules = currentUser?.rules;

  const [ability] = useState(() => buildAbilityFromRules(rules));

  useEffect(() => {
    ability.update(unpackAbilityRules(rules));
  }, [ability, rules]);

  return <AbilityProvider value={ability}>{children}</AbilityProvider>;
}
